import React, {useEffect, useState} from 'react';
import {ItemType} from '../types';
import {
    Icon,
    Input,
    PriorityIcon,
    Scrollbar,
    Text,
    DropdownV2
} from '@priority-software/priority-style-react';
import {isRTL} from '../../../Localization';
import styles from '../styles/searchInput.module.scss';
import {useKeyboardNavigation} from '../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import cx from 'classnames';
import text from '../texts';
import {highlightText} from '../../../_legacy/library/utils/highlightText';

type SearchInputProps = {
    placeholder: string;
    data: ItemType[];
    setFoundItemName: (name: string) => void;
};

export const SearchInput = ({placeholder, data, setFoundItemName}: SearchInputProps) => {
    const [searchValue, setSearchValue] = useState('');
    const [foundItems, setFoundItems] = useState([]);
    const updateSearchValue = (value: string) => {
        let res = [];
        if (value.length) {
            res = data.filter(i => i.Title?.toLowerCase().includes(value.toLowerCase()));
        }
        setSearchValue(value);
        setFoundItems(res);
    };

    useEffect(() => {
        let res = [];
        if (searchValue.length) {
            res = data.filter(i => i.Title.toLowerCase().includes(searchValue.toLowerCase()));
        }
        setFoundItems(res);
    }, [searchValue]);

    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength: foundItems.length,
        pageSize: 1,
        onSelect: () => onSearchItemChoose(foundItems[focusedIndex])
    });

    const onChange = e => {
        updateSearchValue(e.target.value);
    };

    const onIconClick = () => {
        searchValue.length && updateSearchValue('');
    };

    const onSearchItemChoose = (item: ItemType) => {
        resetSearchValue();
        setFoundItemName(item.Name);
    };

    const resetSearchValue = () => {
        updateSearchValue('');
    };

    return (
        <div className={styles.inputWrapper}>
            <DropdownV2
                isOpen={searchValue.length > 0}
                onClose={resetSearchValue}
                width={'fullWidth'}
                contentComponentClasses={styles.dropdown}
                trigger={
                    <Input
                        autoFocus
                        size="large"
                        placeholder={placeholder}
                        value={searchValue}
                        onChange={onChange}
                        componentClasses={styles.searchInput}
                        roundedCorners
                    >
                        <Icon
                            icon={
                                searchValue.length
                                    ? PriorityIcon.closeSmall
                                    : PriorityIcon.searchThin
                            }
                            onClick={onIconClick}
                        />
                    </Input>
                }
                onKeyDown={handleKeyboardNavigation}
            >
                <Scrollbar rtl={isRTL()} autoHeight autoHeightMax={320}>
                    {foundItems.map((item, index) => {
                        return (
                            <Item
                                key={item.Name}
                                item={item}
                                onClick={onSearchItemChoose}
                                isFocused={index === focusedIndex}
                                searchValue={searchValue}
                            />
                        );
                    })}
                    {!foundItems.length && searchValue.length && (
                        <Text
                            textColor="secondary-500"
                            skin="paragraph-2"
                            componentClasses={styles.noResults}
                        >
                            {text.noResults}
                        </Text>
                    )}
                </Scrollbar>
            </DropdownV2>
        </div>
    );
};

const Item = ({item, onClick, isFocused, searchValue}) => {
    return (
        <div
            className={cx(styles.item, {[styles.focused]: isFocused})}
            onClick={() => onClick(item)}
        >
            <Text skin="paragraph-2" textColor="primary-700">
                {highlightText(item.Title, searchValue, true, false)}
            </Text>
        </div>
    );
};
